// AnalyticsTab.tsx
import React from "react";
import {
  RevenueKPIs,
  DailyAnalytics,
  ProviderPerformance,
  ServicePopularity,
  ColorTheme,
} from "../types/revenue.types";
import { SimpleLineChart } from "../charts/SimpleLineChart";
import { SimpleBarChart } from "../charts/SimpleBarChart";
import RevenueService from "../../../services/revenueService";

interface AnalyticsTabProps {
  kpis: RevenueKPIs | null;
  dailyData: DailyAnalytics[];
  providerPerformance: ProviderPerformance[];
  servicePopularity: ServicePopularity[];
  colorTheme: ColorTheme;
}

export const AnalyticsTab: React.FC<AnalyticsTabProps> = ({
  kpis,
  dailyData,
  providerPerformance,
  servicePopularity,
  colorTheme,
}) => {
  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",
  };

  const cardStyle = {
    background: "white",
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  };

  return (
    <div style={gridStyle}>
      {/* Worker Performance */}
      <div style={cardStyle}>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: colorTheme.gray900,
            marginBottom: "16px",
          }}
        >
          👨‍⚕️ Worker Performance
        </h3>
        <SimpleBarChart
          data={providerPerformance}
          labelKey="provider_name"
          valueKey="total_revenue"
          height={300}
          color={colorTheme.success}
        />
      </div>

      {/* Service Popularity */}
      <div style={cardStyle}>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: colorTheme.gray900,
            marginBottom: "16px",
          }}
        >
          ⚕️ Service Popularity
        </h3>
        <SimpleBarChart
          data={servicePopularity}
          labelKey="service_name"
          valueKey="usage_count"
          height={300}
          color={colorTheme.warning}
        />
      </div>

      {/* Revenue Distribution */}
      {kpis && (
        <div style={cardStyle}>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: colorTheme.gray900,
              marginBottom: "16px",
            }}
          >
            Revenue Distribution
          </h3>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: colorTheme.gray600 }}>
                🏥 Clinic Revenue:
              </span>
              <span style={{ fontWeight: "600", color: colorTheme.primary }}>
                {RevenueService.formatCurrency(
                  kpis.current_period.clinic_revenue || 0,
                )}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: colorTheme.gray600 }}>
                👨‍⚕️ Worker Revenue:
              </span>
              <span style={{ fontWeight: "600", color: colorTheme.success }}>
                {RevenueService.formatCurrency(
                  kpis.current_period.provider_revenue || 0,
                )}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: colorTheme.gray600 }}>
                👑 Sponsor Revenue:
              </span>
              <span style={{ fontWeight: "600", color: colorTheme.warning }}>
                {RevenueService.formatCurrency(
                  kpis.current_period.owner_revenue || 0,
                )}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: colorTheme.gray600 }}>
                🤝 Referrer Revenue:
              </span>
              <span style={{ fontWeight: "600", color: "#8b5cf6" }}>
                {RevenueService.formatCurrency(
                  kpis.current_period.referrer_revenue || 0,
                )}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: colorTheme.gray600 }}>
                ❤️ Charity Revenue:
              </span>
              <span style={{ fontWeight: "600", color: colorTheme.charity }}>
                {RevenueService.formatCurrency(
                  kpis.current_period.charity_revenue || 0,
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Daily Revenue Trend */}
      <div style={cardStyle}>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: colorTheme.gray900,
            marginBottom: "16px",
          }}
        >
          Daily Revenue Trend (Last 30 Days)
        </h3>
        <SimpleLineChart
          data={dailyData}
          height={200}
          color={colorTheme.primary}
        />
      </div>
    </div>
  );
};
